import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, Minimize2, Maximize2, Trash2, Copy, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { chatbotAPI } from '../services/api';

const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

const Chatbot = () => {
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [chatInput, setChatInput] = useState('');
    const [chatMessages, setChatMessages] = useState(() => {
        const saved = localStorage.getItem('chatHistory');
        return saved ? JSON.parse(saved) : [
            {
                type: 'bot',
                text: 'Bonjour ! Je suis l\'assistant virtuel de Djakaridia. Comment puis-je vous aider aujourd\'hui ?',
                timestamp: new Date().toISOString()
            }
        ];
    });
    const [isChatLoading, setIsChatLoading] = useState(false);
    const [sessionId] = useState(() => generateUUID());
    const [copiedIndex, setCopiedIndex] = useState(null);

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [chatMessages]);

    useEffect(() => {
        localStorage.setItem('chatHistory', JSON.stringify(chatMessages));
    }, [chatMessages]);

    useEffect(() => {
        if (isChatOpen && !isMinimized) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isChatOpen, isMinimized]);

    const handleChatSubmit = async (e) => {
        e.preventDefault();
        if (!chatInput.trim() || isChatLoading) return;

        const userMessage = chatInput.trim();
        setChatInput('');

        const userMsgObj = {
            type: 'user',
            text: userMessage,
            timestamp: new Date().toISOString()
        };
        setChatMessages(prev => [...prev, userMsgObj]);
        setIsChatLoading(true);

        try {
            const response = await chatbotAPI.sendMessage(userMessage, sessionId);
            const botMsgObj = {
                type: 'bot',
                text: response.response,
                timestamp: new Date().toISOString(),
                suggestions: response.suggestions || []
            };
            setChatMessages(prev => [...prev, botMsgObj]);
        } catch (error) {
            setChatMessages(prev => [...prev, {
                type: 'bot',
                text: "Désolé, je rencontre un problème technique. Veuillez réessayer ou utiliser le formulaire de contact ci-dessous.",
                timestamp: new Date().toISOString(),
                isError: true
            }]);
        } finally {
            setIsChatLoading(false);
        }
    };

    const clearHistory = () => {
        if (confirm('Voulez-vous effacer l\'historique de la conversation ?')) {
            setChatMessages([{
                type: 'bot',
                text: "Historique effacé. Comment puis-je vous aider ?",
                timestamp: new Date().toISOString()
            }]);
            localStorage.removeItem('chatHistory');
        }
    };

    const copyMessage = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="fixed bottom-6 right-6 z-50">
            {isChatOpen ? (
                <div className={`bg-[#1a1f2e] border border-[#1f2937] rounded-lg shadow-2xl transition-all duration-300 ${isMinimized ? 'w-80' : 'w-80 sm:w-96'
                    }`}>
                    {/* Header */}
                    <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-t-lg">
                        <div className="flex items-center justify-between px-4 py-3">
                            <div className="flex items-center gap-2">
                                <div className="relative">
                                    <MessageCircle className="w-5 h-5 text-white" />
                                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                                </div>
                                <span className="font-semibold text-white">Assistant IA</span>
                                <span className="text-xs text-white/80 bg-white/35 px-2 py-0.5 rounded-full">
                                    En ligne
                                </span>
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setIsMinimized(!isMinimized)}
                                    className="hover:bg-emerald-600 p-1 rounded transition-colors text-white"
                                    title={isMinimized ? "Agrandir" : "Réduire"}
                                >
                                    {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
                                </button>
                                <button
                                    onClick={() => setIsChatOpen(false)}
                                    className="hover:bg-emerald-600 p-1 rounded transition-colors text-white"
                                    title="Fermer"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Body */}
                    {!isMinimized && (
                        <>
                            {/* Messages area */}
                            <div className="h-96 overflow-y-auto mb-4 space-y-3 scrollbar-thin scrollbar-thumb-gray-600 p-4">
                                {chatMessages.map((msg, index) => (
                                    <div key={index} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'} group`}>
                                        <div className={`max-w-[85%] ${msg.type === 'user' ? 'order-1' : 'order-1'}`}>
                                            <div className={`relative text-sm px-3 py-2 rounded-lg ${msg.type === 'user'
                                                    ? 'bg-emerald-500 text-white rounded-br-none'
                                                    : msg.isError
                                                        ? 'bg-red-500/10 text-red-400 border border-red-500/30 rounded-bl-none'
                                                        : 'bg-[#0f1419] text-gray-300 border border-[#1f2937] rounded-bl-none'
                                                }`}>
                                                {msg.type === 'bot' && (
                                                    <div className="absolute -left-2 -top-2 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center text-xs">
                                                        🤖
                                                    </div>
                                                )}
                                                <div className="whitespace-pre-wrap break-words text-md">
                                                    {msg.text}
                                                </div>
                                                <div className={`text-xs mt-1 ${msg.type === 'user' ? 'text-emerald-200' : 'text-gray-500'
                                                    }`}>
                                                    {formatTime(msg.timestamp)}
                                                </div>

                                                {/* Bouton copie pour les messages bot */}
                                                {msg.type === 'bot' && !msg.isError && (
                                                    <button
                                                        onClick={() => copyMessage(msg.text, index)}
                                                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                                                        title="Copier"
                                                    >
                                                        {copiedIndex === index ? (
                                                            <Check className="w-3 h-3 text-green-400" />
                                                        ) : (
                                                            <Copy className="w-3 h-3 text-gray-400 hover:text-gray-300" />
                                                        )}
                                                    </button>
                                                )}
                                            </div>

                                            {/* Suggestions */}
                                            {msg.suggestions && msg.suggestions.length > 0 && (
                                                <div className="flex flex-wrap gap-2 mt-2">
                                                    {msg.suggestions.map((suggestion, idx) => (
                                                        <button
                                                            key={idx}
                                                            onClick={() => setChatInput(suggestion)}
                                                            className="text-xs px-2 py-1 bg-[#0f1419] border border-[#1f2937] rounded-full hover:border-emerald-500 hover:text-emerald-400 transition-colors"
                                                        >
                                                            {suggestion}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}

                                {/* Indicateur de frappe */}
                                {isChatLoading && (
                                    <div className="flex justify-start">
                                        <div className="bg-[#0f1419] border border-[#1f2937] rounded-lg rounded-bl-none p-3">
                                            <div className="flex gap-1">
                                                <Loader2 className="w-4 h-4 text-emerald-500 animate-spin" />
                                                <span className="text-sm text-gray-400">L'assistant réfléchit...</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Input area */}
                            <form onSubmit={handleChatSubmit} className="flex gap-2 px-3">
                                <div className="flex-1 relative">
                                    <Input
                                        ref={inputRef}
                                        value={chatInput}
                                        onChange={(e) => setChatInput(e.target.value)}
                                        placeholder="Posez votre question..."
                                        className="bg-[#0f1419] border-[#1f2937] text-white focus:border-emerald-500 pr-8"
                                        disabled={isChatLoading}
                                    />
                                    {chatInput && (
                                        <button
                                            type="button"
                                            onClick={() => setChatInput('')}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                                <Button
                                    type="submit"
                                    size="icon"
                                    className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50"
                                    disabled={isChatLoading || !chatInput.trim()}
                                >
                                    {isChatLoading ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Send className="w-4 h-4" />
                                    )}
                                </Button>
                            </form>

                            {/* Footer */}
                            <div className="flex justify-between items-center mt-3 p-3 border-t border-[#1f2937]">
                                <button
                                    onClick={clearHistory}
                                    className="text-xs text-gray-500 hover:text-red-400 transition-colors flex items-center gap-1"
                                >
                                    <Trash2 className="w-3 h-3" />
                                    Effacer l'historique
                                </button>
                                <div className="text-xs text-gray-500">
                                    {chatMessages.length} message{chatMessages.length > 1 ? 's' : ''}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            ) : (
                <Button
                    onClick={() => setIsChatOpen(true)}
                    size="icon"
                    className="relative group bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-full w-12 h-12 shadow-lg hover:scale-110 transition-all duration-300"
                >
                    <MessageCircle className="w-8 h-8" />
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-xs flex items-center justify-center animate-pulse">
                        !
                    </span>
                </Button>
            )}
        </div>
    );
};

export default Chatbot;